import { Beech } from './beech';
import * as AgentProtocol from './agent-protocol';

// Best-effort snapshot: every subsystem read is wrapped so it never throws.
export function collectEnvironment(beech: Beech): AgentProtocol.AuraConnectEnvironment {
	const data = beech.model.getData();
	const device = beech.twilio.getDevice();

	return {
		capturedAt: new Date().toISOString(),
		app: {
			release: safe(() => beech.config.release) ?? 'unknown',
			environment: safe(() => beech.config.environment) ?? 'unknown',
			userAgent: safe(() => navigator.userAgent) ?? '',
			platform: safe(() => navigator.platform) ?? '',
			isElectron: safe(() => !!window.electronAPI?.isElectron) ?? false,
			embeddedIn: safe(() => beech.embedded.getParentApp()) ?? null
		},
		agent: {
			guid: safe(() => beech.persist.getReconnectToken()) ?? null,
			username: data.username,
			email: safe(() => beech.persist.getLastEmail()) ?? null,
			permissions: safe(() => data.rules.permissions ?? []) ?? [],
			state: data.agentState
		},
		connection: {
			serverState: data.serverState,
			online: safe(() => navigator.onLine) ?? true
		},
		audio: {
			twilioRegistered: safe(() => device?.state === 'registered') ?? false,
			edge: safe(() => device?.edge ?? null) ?? null,
			inputDevice: safe(() => device?.audio?.inputDevice?.label ?? null) ?? null,
			muted: data.agentMuted
		},
		call: data.callLocalSid
			? {
					active: true,
					callSid: data.callLocalSid,
					remoteNumber: data.callRemoteNumber,
					holding: data.callHolding ?? false
				}
			: null,
		settings: safe(() => boundSettings(data.persist)) ?? {}
	};
}

// settings is the only unbounded env field (arbitrary settings:* localStorage), so
// cap it here — at the source, whole values kept — so env stays small and complete.
function boundSettings(raw: Record<string, unknown>): Record<string, unknown> {
	const MAX_KEYS = 100;
	const MAX_VALUE_CHARS = 2048;
	const out: Record<string, unknown> = {};
	for (const key of Object.keys(raw ?? {}).slice(0, MAX_KEYS)) {
		const value = raw[key];
		out[key] =
			typeof value === 'string' && value.length > MAX_VALUE_CHARS ? value.slice(0, MAX_VALUE_CHARS) : value;
	}
	return out;
}

function safe<T>(fn: () => T): T | null {
	try {
		return fn();
	} catch {
		return null;
	}
}
